import React, { useEffect, useMemo, useRef, useState } from "react";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Close from "@material-ui/icons/Close";

import { Competition, IndexedCompetitions } from "types/competition";
import { Picker, Option } from "components/SharedComponents/Picker";

const toOption = (el: Competition): Option => ({
  id: el._id,
  title: el.name,
  active: el.active !== false,
});

export interface GameRuleSelectorProps {
  competitionId: string;
  competitions: Competition[];
  competitionsObj: IndexedCompetitions;
  onChange: (opt: Option) => void;
  onSearchQueryChange?: (query: string) => void;
  onPickerOpen?: () => void;
}

const GameRuleSelector: React.FC<GameRuleSelectorProps> = ({
  competitions,
  competitionsObj,
  competitionId,
  onChange,
  onSearchQueryChange,
  onPickerOpen,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const selectedRule = competitionsObj[competitionId];
  const selectedTitle =
    selectedRule &&
    (selectedRule.active === false
      ? `${selectedRule.name} (unavailable — choose another rule)`
      : selectedRule.name);

  const isMount = useRef(true);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (isMount.current) {
      isMount.current = false;
      return;
    }
    onSearchQueryChange?.(searchQuery);
  }, [searchQuery, onSearchQueryChange]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return competitions;
    return competitions.filter(c => (c.name || "").toLowerCase().includes(q));
  }, [competitions, searchQuery]);

  const totalRules = competitions.length;
  const shownRules = filtered.length;

  const { basic: basicRules, WHS: whsRules, custom: customRules } = filtered.reduce(
    (acc, el) => {
      const typeKey = el.type === "WHS" || el.type === "basic" ? el.type : "custom";
      const opt = toOption(el);
      if (typeKey === "basic") return { ...acc, basic: [...acc.basic, opt] };
      if (typeKey === "WHS") return { ...acc, WHS: [...acc.WHS, opt] };
      return { ...acc, custom: [...acc.custom, opt] };
    },
    { basic: [] as Option[], WHS: [] as Option[], custom: [] as Option[] },
  );

  const clearSearch = () => setSearchInput("");
  const standardCount = basicRules.length + whsRules.length;
  const preferredExpandedPanel = useMemo(() => {
    if (!searchQuery) return false;
    if (shownRules === 0) return false;
    if (standardCount === 0) return 1;
    if (customRules.length === 0) return 0;
    return customRules.length > standardCount ? 1 : 0;
  }, [customRules.length, searchQuery, shownRules, standardCount]);

  return (
    <Picker
      label="Tap to select a game rule"
      dialogTitle="Choose a game rule"
      preferredExpandedPanel={preferredExpandedPanel}
      onOpen={() => {
        setSearchInput("");
        setSearchQuery("");
        onPickerOpen?.();
      }}
      dialogTopContent={
        <div
          style={{
            width: "100%",
            marginBottom: 16,
            boxSizing: "border-box" as const,
          }}
        >
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Search rules"
            placeholder="Type a name to filter"
            value={searchInput}
            onClick={e => e.stopPropagation()}
            onChange={e => setSearchInput(e.target.value)}
            inputProps={{ "aria-label": "Search game rules by name" }}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear search"
                    edge="end"
                    size="small"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      clearSearch();
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Grid container spacing={1} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <Box
                style={{
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 12,
                  padding: 10,
                  background: "rgba(63, 81, 181, 0.03)",
                }}
              >
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 2 }}>
                  Results
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Showing {shownRules} of {totalRules} rules
                </Typography>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                  <Chip size="small" variant="outlined" label={`Basic ${basicRules.length}`} />
                  <Chip size="small" variant="outlined" label={`WHS ${whsRules.length}`} />
                  <Chip size="small" variant="outlined" label={`Custom ${customRules.length}`} />
                </div>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
                  {selectedRule ? `Selected: ${selectedRule.name}` : "Selection: none yet"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </div>
      }
      options={[
        {
          title: "Standard Rules",
          options: [],
          nestedList: [
            {
              title: "Basic",
              data: basicRules,
            },
            {
              title: "WHS",
              data: whsRules,
            },
          ],
        },
        {
          title: "Custom Rules",
          options: customRules,
        },
      ]}
      selectedId={competitionId}
      selectedTitle={selectedTitle || undefined}
      onChange={onChange}
    />
  );
};

export default GameRuleSelector;
